import { owaComputedFn } from "owa-computed-fn";
import { type MailRibbonControlId, type MailRibbonTabId } from "owa-ribbon-ids/lib/mailRibbonId";
import type { RibbonGroupId, RibbonControlId } from "owa-ribbon-ids";
import type { ClientItemId } from "owa-client-ids";
import { getMailRibbonConfigStore } from "owa-mail-ribbon-store/lib/store/store";
import { getDefaultMLRGroupControls } from "./getDefaultMLRGroupControls";
import type { ReadOnlyRibbonControlDefProps } from "./RibbonControlDefinitionProps";
import { type Layout, type VerticalControlStack, sections, singleRow, stack } from "owa-acui";
import { getReadRibbonId } from "./getReadRibbonId";
import { getStringRibbonId } from "owa-ribbon-ids";
import { shouldShowRibbonControl } from "./shouldShowRibbonControl";

export const isMLRGroupCustomized: (groupId: RibbonGroupId, tabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps) => boolean = owaComputedFn(function isMLRGroupCustomized(groupId: RibbonGroupId, tabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps): boolean {
    const controlIds = getMailRibbonConfigStore(props.mailboxInfo)
        .multiline.homeTab.layout.find(g => g.groupId === groupId)
        ?.controlIds.filter(controlId => shouldShowRibbonControl(tabId, controlId, props, false /* isCustomizer */));
    const defaultControlIds = getDefaultMLRGroupControls(groupId, tabId, props);
    
    if (!controlIds || //default group has been removed from the stored ribbon
        controlIds.length !== defaultControlIds.length || // number of controls in the stored group differs from the default
        !controlIds.every((id, index) => id === defaultControlIds[index]) // order of controls differs from the default
    ) {
        return true;
    }
    return false;
});

export const getCustomizedLayoutForGroup: (groupId: RibbonGroupId, tabId: MailRibbonTabId, props: ReadOnlyRibbonControlDefProps, itemId: ClientItemId | undefined) => Layout[] | undefined = (groupId, tabId, props, itemId) => {
    const controlIds = getMailRibbonConfigStore(props.mailboxInfo)
        .multiline.homeTab.layout.find(g => g.groupId === groupId)
        ?.controlIds.filter(controlId => shouldShowRibbonControl(tabId, controlId, props, false /* isCustomizer */));
    
    return getCustomizedLayoutForControlIds(controlIds, itemId);
};

export const getCustomizedLayoutForControlIds = (controlIds: RibbonControlId[] | undefined, itemId: ClientItemId | undefined): Layout[] | undefined => {
    // A single control is always shown as a large button, so no custom layout is needed.
    if (!controlIds || controlIds.length <= 1) {
        return undefined;
    }
    
    const unstackedButtonCount = unstackedButtonCounts[controlIds.length];
    if (unstackedButtonCount) {
        return unstackedButtonCount.map(numLargeButtons => buildLayoutOption(controlIds, itemId, numLargeButtons));
    }
    
    // more than 12 controls: either all unstacked or all stacked
    return [
        buildLayoutOption(controlIds, itemId, controlIds.length),
        sections(singleRow(...stackEveryThree(controlIds, itemId))),
    ];
};

// Layout sections contain rows of controls. A vertical stack is represented by a nested array
// within a single-row section, so stacked control IDs are one array level below normal controls.
//
// layout.sections
//    └─ section
//        └─ row
//            └─ control or vertical stack
export const isControlStacked = (layout: Layout | undefined, controlId: string): boolean => {
    if (!layout) {
        return false;
    }
    
    return layout.sections.some(section => {
        if (!Array.isArray(section) || section.length !== 1) {
            return false;
        }
        
        const row = section[0];
        if (!Array.isArray(row)) {
            return false;
        }
        
        return row.some(control => Array.isArray(control) && control.includes(controlId));
    });
};

// return layout with the specified number of large buttons, and the rest stacked in groups of 3
const buildLayoutOption = (controlIds: RibbonControlId[], itemId: ClientItemId | undefined, numLargeButtons: number): Layout => {
    const layoutControls = controlIds.map(controlId => getLayoutControl(controlId, itemId));
    const largeButtons = layoutControls.slice(0, numLargeButtons);
    const stacks = stackEveryThree(controlIds.slice(numLargeButtons), itemId);
    return sections(singleRow(...largeButtons, ...stacks));
};

const stackEveryThree = (controlIds: RibbonControlId[], itemId: ClientItemId | undefined): VerticalControlStack[] => {
    const stacks: VerticalControlStack[] = [];
    for (let i = 0; i < controlIds.length; i += 3) {
        const chunk = controlIds.slice(i, i + 3).map(id => getLayoutControl(id, itemId));
        stacks.push(stack(chunk[0], chunk[1] ?? null, chunk[2] ?? null));
    }
    return stacks;
};

// how many unstacked buttons precede the stacked groups for each group size at various screen widths
// <group size> : [<large width count>, <medium width count>, <small width count>]
const unstackedButtonCounts: {
    [key: number]: number[];
} = {
    2: [2, 0],
    3: [3, 0],
    4: [4, 1],
    5: [5, 2],
    6: [3, 0],
    7: [7, 1],
    8: [5, 2],
    9: [9, 0],
    10: [10, 1],
    11: [11, 5, 2],
    12: [4, 1],
};

const getLayoutControl: (controlId: RibbonControlId, itemId: ClientItemId | undefined) => string = owaComputedFn(function getLayoutControl(controlId: RibbonControlId, itemId: ClientItemId | undefined): string {
    switch (controlId) {
        case 519:
        case 657:
        case 559:
        case 556:
        case 557:
        case 555:
        case 678:
        case 686:
        case 712:
        case 552:
        case 527:
        case 616:
        case 549:
        case 697:
        case 731:
        case 709:
            return getReadRibbonId(controlId, itemId).toString();
        default:
            return getStringRibbonId(controlId);
    }
});
